import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Award, Flame, Play, Square, Timer, Volume2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { playBigCheers } from "@/lib/sfx";
import { playSay, playSayHighlight, stopReading } from "@/lib/readingAudio";
import KikoCheer from "@/components/KikoCheer";
import StoryRecorder from "@/components/reading/StoryRecorder";
import { badgeName } from "@/components/reading/LibraryBadges";
import LibraryReadAloud from "@/components/reading/LibraryReadAloud";
import { ReadingBg, Btn } from "@/pages/ReadingHome";
import { L3_LINES, readMsg } from "@/pages/ReadingLevel3";

const PART = { A: { fr: "Syllabes simples", en: "Simple sounds" }, B: { fr: "Texte du son", en: "Sound text" },
  R: { fr: "Texte de révision", en: "Revision text" }, ADV: { fr: "Texte avancé", en: "Advanced text" }, LIB: { fr: "Bibliothèque", en: "Library" } };
const LIB_MSG = {
  fr: (s) => `Bravo ! Tu as lu le texte en ${s}.`, en: (s) => `Well done! You read the text in ${s}.` };
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

// The text, sentence by sentence: tap one for Kiko to read it, or let Kiko read everything while the words light up
function TextBody({ t, lang, stopSignal, onRead }) {
  const [act, setAct] = useState(-1);
  const [hi, setHi] = useState(-1);
  const token = useRef(0);
  useEffect(() => () => { token.current += 1; stopReading(); }, []);
  useEffect(() => { if (stopSignal) { token.current += 1; stopReading(); setAct(-1); setHi(-1); } }, [stopSignal]);
  const read = async (from, all) => {
    const my = ++token.current;
    onRead();
    for (let k = from; k < (all ? t.sentences.length : from + 1); k += 1) {
      if (my !== token.current) return;
      setAct(k);
      await playSayHighlight(t.sentences[k], lang, (w) => my === token.current && setHi(w));
    }
    if (my === token.current) { setAct(-1); setHi(-1); }
  };
  const stop = () => { token.current += 1; stopReading(); setAct(-1); setHi(-1); };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        {act < 0
          ? <Btn onClick={() => read(0, true)} testid="text-listen-all"><Volume2 size={18} /> {L3_LINES[lang].listen}</Btn>
          : <Btn onClick={stop} testid="text-listen-stop"><Square size={16} /> {lang === "fr" ? "Stop" : "Stop"}</Btn>}
      </div>
      <p className="text-xl sm:text-2xl leading-loose" data-testid="text-body">
        {t.sentences.map((s, k) => (
          <span key={k} onClick={() => read(k, false)} className={`cursor-pointer rounded-lg ${act === k ? "bg-yellow-50" : "hover:bg-sky-50"}`} data-testid={`text-sentence-${k}`}>
            {s.split(/\s+/).map((w, j) => <span key={j} className={`rounded px-0.5 transition-colors duration-100 ${act === k && hi === j ? "bg-yellow-200 text-blue-700" : ""}`}>{w} </span>)}
          </span>
        ))}
      </p>
    </div>
  );
}

// 3 readings against the clock (under 2 minutes), like the Level 3 texts
function Chrono({ childId, t, lang, onResult, onBegin }) {
  const L = L3_LINES[lang];
  const [tryN, setTryN] = useState(0);
  const [start, setStart] = useState(null);
  const [now, setNow] = useState(0);
  const tick = useRef(null);
  useEffect(() => () => clearInterval(tick.current), []);
  const begin = () => { onBegin(); setStart(Date.now()); setNow(Date.now()); tick.current = setInterval(() => setNow(Date.now()), 250); };
  const finish = async () => {
    clearInterval(tick.current);
    const secs = Math.round((Date.now() - start) / 1000);
    setStart(null);
    try {
      const { data } = await api.post(`/reading/${childId}/text/${t.id}/read`, { seconds: secs });
      onResult(data);
      setTryN((n) => Math.min(n + 1, 2));
    } catch { toast.error(lang === "fr" ? "Oups, réessaie." : "Oops, try again."); }
  };
  const secs = start ? Math.floor((now - start) / 1000) : 0;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="rounded-full bg-slate-900 text-white px-4 py-2 font-bold" data-testid="text-try">{t.library ? (lang === "fr" ? "Chronomètre (facultatif)" : "Timer (optional)") : L.tries[tryN]}</span>
      {start ? (
        <>
          <span className={`text-3xl font-bold tabular-nums ${t.limit && secs > t.limit ? "text-red-600" : "text-green-700"}`} data-testid="text-timer"><Timer className="inline -mt-1" /> {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}</span>
          <Btn onClick={finish} testid="text-done" color="bg-green-500 text-white">{L.done}</Btn>
        </>
      ) : <Btn onClick={begin} testid="text-start" color="bg-blue-600 text-white"><Play size={18} /> {L.start}</Btn>}
    </div>
  );
}

export default function ReadingText() {
  const { childId, textId } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [res, setRes] = useState(null);
  const [kiko, setKiko] = useState(null);
  const [stopSig, setStopSig] = useState(0);
  const vRef = useRef(null);
  const lang = textId.slice(1, 3).toLowerCase();
  const fr = lang === "fr";
  const back = () => navigate(`/kid/${childId}/reading/${lang}`);
  useEffect(() => { api.get(`/reading/${childId}/text/${textId}`).then(({ data }) => setT(data)).catch(back); }, [childId, textId]); // eslint-disable-line react-hooks/exhaustive-deps
  const onResult = (r) => {
    setRes(r);
    if (r.library) {
      if (r.new_badge) { playBigCheers(); setKiko({ n: Date.now(), msg: fr ? `Nouveau badge : ${badgeName(r.new_badge, lang)} ! ${r.new_badge} textes lus !` : `New badge: ${badgeName(r.new_badge, lang)}! ${r.new_badge} texts read!`, kind: "win", streak: 10 }); }
      else if (r.passed && r.proven) { playBigCheers(); setKiko({ n: Date.now(), msg: LIB_MSG[lang](mmss(r.seconds)), kind: "win", streak: 10 }); }
      else if (!r.passed) playSay(L3_LINES[lang].fast, lang);
      return;
    }
    const msg = readMsg(L3_LINES[lang], r);
    if (r.passed) { playBigCheers(); setKiko({ n: Date.now(), msg, kind: "win", streak: 10 }); }
    playSay(msg, lang);
  };
  if (!t) return <ReadingBg><div className="p-10 text-center font-bold">…</div></ReadingBg>;
  return (
    <ReadingBg>
      <header className="max-w-4xl mx-auto px-5 pt-5 flex items-center gap-3">
        <button onClick={back} className="w-11 h-11 rounded-full bg-white border-[3px] border-slate-900 flex items-center justify-center" data-testid="text-back"><ArrowLeft /></button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{PART[t.part][lang]} · {t.focus}</p>
          <h1 className="text-xl sm:text-2xl font-bold truncate" data-testid="text-title">{t.title}</h1>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-5 py-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl bg-white border-[3px] border-slate-900 p-6 sm:p-8 shadow-[6px_6px_0_0_#0F172A] space-y-6" data-testid="text-card">
          {t.video
            ? <video ref={vRef} src={t.video} poster={t.picture} controls controlsList="nodownload" disablePictureInPicture playsInline preload="metadata" onPlay={() => setStopSig((n) => n + 1)} className="w-full rounded-2xl border-[3px] border-slate-900 bg-black" data-testid="text-video" />
            : <img src={t.picture} alt={t.title} className="w-full max-h-72 object-contain rounded-2xl" data-testid="text-picture" />}
          {t.library
            ? <p className="text-lg sm:text-xl leading-relaxed whitespace-pre-line" data-testid="text-body">{t.text}</p>
            : <TextBody t={t} lang={lang} stopSignal={stopSig} onRead={() => vRef.current?.pause()} />}
          {t.library
            ? <LibraryReadAloud childId={childId} t={t} lang={lang} onResult={onResult} onBegin={() => vRef.current?.pause()} />
            : <Chrono childId={childId} t={t} lang={lang} onResult={onResult} onBegin={() => { vRef.current?.pause(); setStopSig((n) => n + 1); }} />}
          {res && (
            <p className={`rounded-2xl border-[3px] border-slate-900 p-4 font-bold text-lg ${res.passed ? "bg-green-200" : "bg-amber-100"}`} data-testid="text-result">
              {res.library ? (res.passed ? LIB_MSG[lang](mmss(res.seconds)) : L3_LINES[lang].fast) : `${readMsg(L3_LINES[lang], res)} (${mmss(res.seconds)})`}
            </p>
          )}
          {res?.new_badge && (
            <p className="rounded-2xl border-[3px] border-slate-900 bg-amber-200 p-4 font-bold text-lg flex items-center gap-2" data-testid="text-new-badge">
              <Award /> {fr ? `Nouveau badge : ${badgeName(res.new_badge, lang)} (${res.new_badge} textes lus)` : `New badge: ${badgeName(res.new_badge, lang)} (${res.new_badge} texts read)`}
            </p>
          )}
          {res?.library && res.passed && !res.proven && (
            <p className="rounded-2xl border-2 border-amber-500 bg-amber-50 p-3 text-sm font-semibold" data-testid="text-not-proven">
              {fr ? "Bravo ! Mais sans ta voix enregistrée, cette lecture ne compte pas pour les badges ni la série." : "Well done! But without your recorded voice, this reading does not count for badges or the streak."}
            </p>
          )}
          {res?.library && res.streak && (
            <p className="rounded-2xl border-[3px] border-slate-900 bg-orange-100 p-3 font-bold flex items-center gap-2" data-testid="text-streak">
              <Flame className="text-orange-600" /> {fr ? `Série : ${res.streak.current} jour(s) de suite` : `Streak: ${res.streak.current} day(s) in a row`}
            </p>
          )}
          {!t.library && <StoryRecorder childId={childId} storyId={t.id} page={0} fr={fr} />}
        </motion.div>
      </main>
      <KikoCheer cheer={kiko} />
    </ReadingBg>
  );
}
